/**
 * DED-973: Entity Linked Documents Section
 * A reusable component for linking documents to Findings and Corrective Actions
 * Documents inherit/append controls from the parent entity
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Plus, ExternalLink, Trash2, Link as LinkIcon, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useTenancy } from '@/contexts/TenancyContext';
import { toast } from 'sonner';
import { getDocument, saveDocument } from '@/services/documentsService';
import { renderDate } from '@shared/renderDate';
import { DocumentsCombobox, DocumentsProvider } from '@/components/ui/DocumentsCombobox';
import { Label } from '@/components/ui/label';

interface LinkedDocument {
  docID: string;
  fileName: string;
  displayName?: string;
  documentType?: string;
  nextReviewDate?: Date | string;
  lastReviewDate?: Date | string;
}

interface EntityLinkedDocumentsSectionProps {
  /** The type of entity - determines the default document type */
  entityType: 'Finding' | 'Corrective Action';
  /** Current linked document IDs */
  linkedDocuments: string[];
  /** Callback when documents change */
  onDocumentsChange: (docIds: string[]) => void;
  /** Controls to inherit/append to linked documents */
  inheritedControls?: string[];
  /** Title for the entity (used in document descriptions) */
  entityTitle?: string;
  /** Owner of the entity to inherit to documents */
  entityOwner?: string;
  /** Whether the section is read-only */
  isReadOnly?: boolean;
}

export const EntityLinkedDocumentsSection: React.FC<EntityLinkedDocumentsSectionProps> = ({
  entityType,
  linkedDocuments = [],
  onDocumentsChange,
  inheritedControls = [],
  entityTitle,
  entityOwner,
  isReadOnly = false,
}) => {
  const { activeTenancy } = useTenancy();
  const [documents, setDocuments] = useState<LinkedDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [selectedDocsToLink, setSelectedDocsToLink] = useState<string[]>([]);
  const [showUnlinkDialog, setShowUnlinkDialog] = useState(false);
  const [docToUnlink, setDocToUnlink] = useState<string | null>(null);

  // Fetch document details for display
  const fetchDocuments = useCallback(async (docIdsToFetch?: string[]) => {
    const idsToFetch = docIdsToFetch || linkedDocuments;

    if (!activeTenancy?.id || idsToFetch.length === 0) {
      setDocuments([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const fetchedDocs: LinkedDocument[] = [];

      for (const docId of idsToFetch) {
        try {
          const docData = await getDocument(docId, activeTenancy.id);
          if (docData) {
            fetchedDocs.push({
              docID: docId,
              fileName: docData.fileName || (docData as any).title || 'Untitled',
              displayName: (docData as any).displayName || (docData as any).title || docData.fileName,
              documentType: docData.documentType,
              nextReviewDate: docData.nextReviewDate as any,
              lastReviewDate: docData.lastReviewDate as any,
            });
          }
        } catch (err) {
          console.error(`[EntityLinkedDocumentsSection] Error fetching document ${docId}:`, err);
        }
      }

      setDocuments(fetchedDocs);
    } catch (error) {
      console.error('[EntityLinkedDocumentsSection] Error fetching linked documents:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTenancy?.id, linkedDocuments]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Handle creating a new document
  const handleCreateDocument = () => {
    const today = new Date();
    const nextReviewDate = new Date(today);
    nextReviewDate.setFullYear(nextReviewDate.getFullYear() + 1);

    const prefillData = {
      documentType: entityType,
      status: 'Draft',
      title: entityTitle ? `${entityType}: ${entityTitle}` : `${entityType} Document`,
      description: entityTitle
        ? `Supporting document for ${entityType.toLowerCase()}: ${entityTitle}`
        : `Supporting document for ${entityType.toLowerCase()}`,
      dateAdded: renderDate(today),
      lastReviewDate: today.toISOString(),
      nextReviewDate: nextReviewDate.toISOString(),
      // DED-973: Inherit controls and owner from the parent entity
      controls: inheritedControls,
      owner: entityOwner,
    };

    // Store prefill data and open document editor in new tab
    sessionStorage.setItem('documentPrefillData', JSON.stringify(prefillData));
    sessionStorage.setItem('documentPrefillCallback', JSON.stringify({
      type: 'entityLinkedDocument',
      entityType,
    }));
    window.open('/documentdetail/new', '_blank');
    
    toast.info('Document editor opened in new tab. Link the document after saving.');
  };

  // Handle linking existing documents
  const handleLinkDocuments = async () => {
    if (selectedDocsToLink.length === 0) return;

    try {
      // DED-973: Update each linked document with inherited controls, owner, and review dates
      if (activeTenancy?.id) {
        const today = new Date();
        const nextReviewDate = new Date(today);
        nextReviewDate.setFullYear(nextReviewDate.getFullYear() + 1);

        for (const docId of selectedDocsToLink) {
          try {
            const docData = await getDocument(docId, activeTenancy.id);
            if (docData) {
              const existingControls = (docData as any).controls || [];
              // Append controls (don't duplicate)
              const mergedControls = [...new Set([...existingControls, ...inheritedControls])];
              await saveDocument({
                ...docData,
                controls: mergedControls,
                owner: entityOwner || docData.owner,
                lastReviewDate: today.toISOString(),
                nextReviewDate: nextReviewDate.toISOString(),
              } as any, null, activeTenancy.id);
            }
          } catch (err) {
            console.error(`[EntityLinkedDocumentsSection] Error updating controls for document ${docId}:`, err);
          }
        }
      }

      const newLinkedDocs = [...linkedDocuments, ...selectedDocsToLink];
      onDocumentsChange(newLinkedDocs);
      await fetchDocuments(newLinkedDocs);
      toast.success(`Linked ${selectedDocsToLink.length} document(s)`);
      setShowLinkDialog(false);
      setSelectedDocsToLink([]);
    } catch (error) {
      console.error('[EntityLinkedDocumentsSection] Error linking documents:', error);
      toast.error('Failed to link documents');
    }
  };

  // Handle unlinking a document
  const handleUnlinkDocument = async (docId: string) => {
    try {
      const newLinkedDocs = linkedDocuments.filter(id => id !== docId);
      onDocumentsChange(newLinkedDocs);
      toast.success('Document unlinked');
      setShowUnlinkDialog(false);
      setDocToUnlink(null);
      await fetchDocuments(newLinkedDocs);
    } catch (error) {
      console.error('[EntityLinkedDocumentsSection] Error unlinking document:', error);
      toast.error('Failed to unlink document');
    }
  };

  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Linked Documents
            {documents.length > 0 && (
              <Badge variant="secondary" className="ml-1">
                {documents.length}
              </Badge>
            )}
          </Label>
          {!isReadOnly && (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowLinkDialog(true)}
              >
                <LinkIcon className="h-3 w-3 mr-1" />
                Link Existing
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCreateDocument}
              >
                <Plus className="h-3 w-3 mr-1" />
                Create New
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-4">
            <div className="h-4 w-4 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            <span className="ml-2 text-xs text-muted-foreground">Loading...</span>
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-4 border rounded-lg border-dashed">
            <FileText className="mx-auto h-8 w-8 text-muted-foreground/50 mb-2" />
            <p className="text-xs text-muted-foreground">No linked documents</p>
            {!isReadOnly && (
              <p className="text-xs text-muted-foreground mt-1">
                Click "Create New" or "Link Existing" to add documents
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {documents.map(doc => (
              <div
                key={doc.docID}
                className="flex items-center justify-between p-2 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <FileText className="h-4 w-4 text-primary shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-xs truncate">
                      {doc.displayName || doc.fileName}
                    </p>
                    {doc.documentType && (
                      <Badge variant="outline" className="text-xs mt-0.5">
                        {doc.documentType}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => window.open(`/documentdetail/${doc.docID}`, '_blank')}
                    title="Open in new tab"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                  {!isReadOnly && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                      onClick={() => {
                        setDocToUnlink(doc.docID);
                        setShowUnlinkDialog(true);
                      }}
                      title="Unlink document"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Link Existing Documents Dialog */}
      <Dialog
        open={showLinkDialog}
        onOpenChange={open => {
          setShowLinkDialog(open);
          if (!open) setSelectedDocsToLink([]);
        }}
      >
        <DialogContent className="sm:max-w-[500px] bg-card overflow-visible">
          <DialogHeader>
            <DialogTitle>Link Existing Documents</DialogTitle>
            <DialogDescription>
              Select documents from your document library to link to this {entityType.toLowerCase()}.
              <span className="block mt-2 text-xs">
                Documents will be updated with:
                {entityOwner && <span className="block">• Owner: {entityOwner}</span>}
                {inheritedControls.length > 0 && <span className="block">• {inheritedControls.length} control(s)</span>}
                <span className="block">• Last review date: Today</span>
                <span className="block">• Next review date: 1 year from today</span>
              </span>
            </DialogDescription>
          </DialogHeader>
          {/* DED-973: Container with min-height to ensure popover dropdown has space */}
          <div className="py-4 min-h-[120px]">
            <DocumentsProvider>
              <DocumentsCombobox
                selected={selectedDocsToLink}
                setSelected={setSelectedDocsToLink}
                excludeIds={linkedDocuments}
                placeholder="Search and select documents..."
                fullWidth
              />
            </DocumentsProvider>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowLinkDialog(false);
                setSelectedDocsToLink([]);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleLinkDocuments}
              disabled={selectedDocsToLink.length === 0}
            >
              Link {selectedDocsToLink.length > 0 ? `(${selectedDocsToLink.length})` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlink Confirmation Dialog */}
      <Dialog open={showUnlinkDialog} onOpenChange={setShowUnlinkDialog}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle>Unlink Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to unlink this document from the {entityType.toLowerCase()}? The document will not be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowUnlinkDialog(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => docToUnlink && handleUnlinkDocument(docToUnlink)}
            >
              Unlink
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EntityLinkedDocumentsSection;
